import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useMemo, useState } from "react";
import FroalaEditorWithDelay from "./FroalaEditorWithDelay";
import ResultOptions from "@/components/comm-generator/ResultOptions";

export interface ResultItem {
  language: string;
  body: string;
  type: string;
  format: string;
  style?: string;
  trackingIds?: string[]; // Optional tracking IDs for changes
  references?: string[] | null; // References from the ToolResponse
}

const TAB_STYLE =
  "px-4 py-3 transition-all !rounded-none relative bg-light data-[state=active]:text-primary data-[state=active]:shadow-none after:absolute after:content-[''] after:h-0.5 after:bg-primary after:bottom-0 after:left-[15%] after:right-[15%] after:rounded-t-md after:opacity-0 data-[state=active]:after:opacity-100 after:transition-opacity after:duration-200 data-[state=active]:bg-light";

function MultiTabResult({
  results,
  references,
  resetSignal,
}: {
  results: ResultItem[];
  references?: string[] | null;
  resetSignal?: number;
}) {
  // ignore wrapper/whitespace differences
  const semanticEqual = (a?: string, b?: string): boolean => {
    const sa = a || "";
    const sb = b || "";
    if (sa === sb) return true;
    try {
      if (typeof window !== "undefined" && "DOMParser" in window) {
        const parser = new DOMParser();
        const da = parser.parseFromString(sa, "text/html");
        const db = parser.parseFromString(sb, "text/html");
        const ta = (
          da.body?.textContent ||
          da.documentElement.textContent ||
          ""
        )
          .replace(/\s+/g, " ")
          .trim();
        const tb = (
          db.body?.textContent ||
          db.documentElement.textContent ||
          ""
        )
          .replace(/\s+/g, " ")
          .trim();
        if (ta === tb) return true;
      }
      // Fallback normalization
      const norm = (s: string) =>
        s
          .replace(/<!DOCTYPE[\s\S]*?>/gi, "")
          .replace(/<\/?html[^>]*>/gi, "")
          .replace(/<\/?body[^>]*>/gi, "")
          .replace(/>\s+</g, "><")
          .replace(/\s+/g, " ")
          .trim();
      return norm(sa) === norm(sb);
    } catch {
      return false;
    }
  };

  // no-op: removed debug logging of results
  const groupedResults = useMemo(() => {
    const grouped: Record<string, Record<string, ResultItem>> = {};

    results.forEach((result) => {
      if (!grouped[result.type]) {
        grouped[result.type] = {};
      }
      grouped[result.type][result.language] = result;
    });

    return grouped;
  }, [results]);

  const [editedContent, setEditedContent] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    setEditedContent((prev) => {
      const next: Record<string, string> = { ...prev };
      for (const r of results) {
        const key = `${r.type}-${r.language}`;
        const hadEdited = next[key] !== undefined;
        const prevEditedVal = next[key];

        if (!hadEdited) {
          next[key] = r.body;
          continue;
        }

        if (
          semanticEqual(prevEditedVal, r.body) === false &&
          semanticEqual(prevEditedVal, "")
        ) {
          // edge: if editor wrote empty while result has content, prefer server
          next[key] = r.body;
          continue;
        }
      }
      return next;
    });
  }, [results]);

  const defaultType = useMemo(
    () => Object.keys(groupedResults)[0],
    [groupedResults]
  );

  const [selectedType, setSelectedType] = useState<string | undefined>(
    undefined
  );
  const [selectedLanguageByType, setSelectedLanguageByType] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!defaultType) {
      setSelectedType(undefined);
      return;
    }
    setSelectedType((prev) => {
      const next = prev && groupedResults[prev] ? prev : defaultType;
      return next;
    });
  }, [defaultType, groupedResults]);
  useEffect(() => {
    setSelectedLanguageByType((prev) => {
      const next = { ...prev };
      for (const [t, langs] of Object.entries(groupedResults)) {
        const first = Object.keys(langs)[0];
        if (!next[t] || !(next[t] in langs)) {
          next[t] = first;
        }
      }
      return next;
    });
  }, [groupedResults]);

  useEffect(() => {
    if (resetSignal === undefined) return;
    const firstType = Object.keys(groupedResults)[0];
    if (!firstType) return;
    const firstLang = Object.keys(groupedResults[firstType] || {})[0];
    setSelectedType(firstType);
    if (firstLang) {
      setSelectedLanguageByType((prev) => ({
        ...prev,
        [firstType]: firstLang,
      }));
    }
  }, [resetSignal, groupedResults]);

  const handleContentChange = (
    type: string,
    language: string,
    newContent: string
  ) => {
    const contentKey = `${type}-${language}`;
    setEditedContent((prev) => ({
      ...prev,
      [contentKey]: newContent,
    }));
  };

  return (
    <Tabs
      value={selectedType}
      onValueChange={(val) => {
        setSelectedType(val);
      }}
      className="flex flex-col h-full"
    >
      <TabsList
        className={
          Object.keys(groupedResults).length <= 1
            ? "hidden"
            : "flex flex-wrap justify-start w-full"
        }
      >
        {Object.keys(groupedResults).map((type) => (
          <TabsTrigger key={type} value={type} className={TAB_STYLE}>
            {type.capitalize()}
          </TabsTrigger>
        ))}
      </TabsList>

      {Object.entries(groupedResults).map(([type, languageResults]) => {
        const defaultLanguage = Object.keys(languageResults)[0];
        const selectedLanguage =
          selectedLanguageByType[type] || defaultLanguage;

        return (
          <TabsContent value={type} key={type} className="h-full">
            <Tabs
              value={selectedLanguage}
              onValueChange={(val) => {
                setSelectedLanguageByType((prev) => ({ ...prev, [type]: val }));
              }}
              className="h-full"
            >
              <TabsList
                className={
                  Object.keys(languageResults).length <= 1
                    ? "hidden"
                    : "flex flex-wrap justify-start w-full"
                }
              >
                {Object.keys(languageResults).map((language) => (
                  <TabsTrigger
                    key={`${type}-${language}`}
                    value={language}
                    className={TAB_STYLE}
                  >
                    {language.toUpperCase()}
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.entries(languageResults).map(([language, result]) => {
                const contentKey = `${type}-${language}`;
                const edited = editedContent[contentKey];
                const usedEdited =
                  edited !== undefined &&
                  !(edited === "" && (result.body?.length || 0) > 0);
                const currentContent = usedEdited ? edited : result.body;
                return (
                  <TabsContent
                    value={language}
                    key={`${type}-${language}`}
                    className="data-[state=active]:flex flex-col flex-grow h-full sm:h-full md:h-full"
                  >
                    {selectedLanguage === language && (
                      <>
                        <FroalaEditorWithDelay
                          content={currentContent}
                          type={type}
                          language={language}
                          style={result.style}
                          trackingIds={result.trackingIds}
                          references={references || result.references}
                          onContentChange={handleContentChange}
                        />
                        <ResultOptions
                          style={result.style}
                          currentContent={currentContent}
                          format={result.format}
                          references={references || result.references}
                        />
                      </>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          </TabsContent>
        );
      })}
    </Tabs>
  );
}

export default MultiTabResult;
